(function (root, factory) {
  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
    return;
  }

  root.theme = root.theme || {};
  root.theme.search = Object.freeze(api);
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const MODEL_PREFIXES = Object.freeze([
    'BB', 'BL', 'BR', 'CN', 'CS', 'EW', 'FC', 'FD', 'FH', 'HB', 'PD', 'RD', 'SL', 'SM', 'ST', 'SW', 'WH'
  ]);
  const INNER_TUBE_TERMS = Object.freeze(['ic lastik', 'ic lastigi', 'sambrel', 'inner tube']);
  const STOCK_FALLBACK_INTENTS = Object.freeze(new Set([
    '29 ic lastik',
    '29 jant ic lastik',
    '29 sambrel',
    '29 inner tube'
  ]));
  const STOCK_FALLBACK_DIAMETERS = Object.freeze(['26', '27', '27.5', '28', '29', '700']);
  const INNER_TUBE_NEGATIVE_TERMS = Object.freeze([
    'dis lastik',
    'dis lastigi',
    'jant seti',
    'jant cemberi',
    'wheelset',
    'wheel',
    'rim',
    'bisiklet janti',
    'jant bandi',
    'tubeless',
    'lastik sutu',
    'yama',
    'tamir kiti',
    'tamir seti',
    'tamir spreyi',
    'pompa',
    'levye',
    'aksesuar',
    'komple bisiklet',
    'dag bisikleti',
    'yol bisikleti',
    'cocuk bisikleti',
    'elektrikli bisiklet',
    'sehir bisikleti'
  ]);
  const MAX_QUERY_LENGTH = 128;
  const TURKISH_ASCII_PAIRS = Object.freeze([
    ['iç', 'ic'],
    ['çocuk', 'cocuk'],
    ['şambrel', 'sambrel'],
    ['boğazı', 'bogazi'],
    ['dış', 'dis']
  ]);

  function cleanQuery(value) {
    const input = String(value == null ? '' : value);
    const normalized = typeof input.normalize === 'function' ? input.normalize('NFKC') : input;
    return normalized.trim().replace(/\s+/g, ' ');
  }

  function fold(value) {
    return cleanQuery(value)
      .toLocaleLowerCase('tr-TR')
      .replace(/ı/g, 'i')
      .replace(/ç/g, 'c')
      .replace(/ğ/g, 'g')
      .replace(/ö/g, 'o')
      .replace(/ş/g, 's')
      .replace(/ü/g, 'u')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  function compact(value) {
    return fold(value).replace(/[^a-z0-9]/g, '');
  }

  function unique(values) {
    const seen = new Set();
    return values.filter((value) => {
      const key = cleanQuery(value).toLocaleLowerCase('tr-TR');
      if (!value || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  function hasAdvancedSearchSyntax(value) {
    const input = cleanQuery(value);
    return input.length > MAX_QUERY_LENGTH
      || /["':]/u.test(input)
      || /(^|\s)-(?!\d)/u.test(input)
      || /(^|\s)(?:AND|OR|NOT)(?=\s|$)/iu.test(input);
  }

  function escapeRegExp(value) {
    return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function replacePhrase(value, phrase, replacement) {
    const pattern = new RegExp(
      `(^|[^\\p{L}\\p{N}])${escapeRegExp(phrase)}(?=$|[^\\p{L}\\p{N}])`,
      'iu'
    );
    return String(value).replace(pattern, `$1${replacement}`);
  }

  function codeParts(value) {
    const original = cleanQuery(value);
    if (!original || original.length > 32 || /[^\p{L}\p{N}._\-\s]/u.test(original)) return null;

    const upper = fold(original).toUpperCase();
    const compactCode = upper.replace(/[^A-Z0-9]/g, '');
    if (compactCode.length < 3 || !/[A-Z]/.test(compactCode) || !/\d/.test(compactCode)) return null;

    const separated = upper.split(/[\s_-]+/).filter(Boolean);
    if (separated.length > 3) return null;
    if (separated.length > 1 && MODEL_PREFIXES.includes(separated[0])) {
      const tail = separated.slice(1).join('-');
      if (/[A-Z]/.test(tail) && /\d/.test(tail)) {
        return { compact: compactCode, head: separated[0], tail };
      }
    }
    if (separated.length > 1) return null;

    const head = MODEL_PREFIXES.find((prefix) => compactCode.startsWith(prefix));
    if (head) {
      const tail = compactCode.slice(head.length);
      if (/[A-Z]/.test(tail) && /\d/.test(tail)) return { compact: compactCode, head, tail };
    }

    return { compact: compactCode, head: '', tail: compactCode };
  }

  function codeCandidates(value) {
    const original = cleanQuery(value);
    const parts = codeParts(original);
    if (!parts) return [original].filter(Boolean);
    if (!parts.head) return [original];

    const tailWithDashes = parts.tail.replace(/[\s_]+/g, '-');
    const tailCompact = tailWithDashes.replace(/-/g, '');
    return unique([
      original,
      `${parts.head}-${tailWithDashes}`,
      `${parts.head} ${tailWithDashes.replace(/-/g, ' ')}`,
      `${parts.head}${tailCompact}`,
      tailWithDashes
    ]).slice(0, 4);
  }

  function hasPhrase(value, phrase) {
    const haystack = ` ${fold(value).replace(/[^a-z0-9]+/g, ' ')} `;
    const needle = ` ${fold(phrase).replace(/[^a-z0-9]+/g, ' ')} `;
    return haystack.includes(needle);
  }

  function isInnerTubeIntent(value) {
    return INNER_TUBE_TERMS.some((term) => hasPhrase(value, term));
  }

  function naturalFallback(value) {
    const original = cleanQuery(value);

    if (!original || hasAdvancedSearchSyntax(original)) return '';

    if (isInnerTubeIntent(original)) {
      const size = extractSizeIntent(original);
      if (size.width != null) return '';

      const aliases = [
        'jant iç lastiği',
        'jant ic lastigi',
        'jant iç lastik',
        'jant ic lastik',
        'iç lastiği',
        'ic lastigi',
        'ic lastik',
        'şambrel',
        'sambrel',
        'inner tube'
      ];
      for (const alias of aliases) {
        const fallback = replacePhrase(original, alias, 'iç lastik');
        if (fallback !== original) return cleanQuery(fallback);
      }
      return '';
    }

    for (const [turkish, ascii] of TURKISH_ASCII_PAIRS) {
      const withTurkish = replacePhrase(original, ascii, turkish);
      if (withTurkish !== original) return withTurkish;

      const withAscii = replacePhrase(original, turkish, ascii);
      if (withAscii !== original) return withAscii;
    }

    return '';
  }

  function candidates(value) {
    const original = cleanQuery(value);
    if (!original) return [];
    if (hasAdvancedSearchSyntax(original)) return [original];
    const parts = codeParts(original);
    if (parts) return codeCandidates(original);
    return unique([original, naturalFallback(original)]).slice(0, 2);
  }

  function stockFallbackQuery(value) {
    const query = fold(value);
    return STOCK_FALLBACK_INTENTS.has(query) ? '29 iç lastik' : '';
  }

  function list(value) {
    if (Array.isArray(value)) return value.filter(Boolean).map(String);
    if (!value) return [];
    return String(value).split('|').map((item) => item.trim()).filter(Boolean);
  }

  function productSearchText(product) {
    return [
      product.title,
      product.productType,
      product.vendor,
      ...list(product.tags),
      ...list(product.variantTitles),
      ...list(product.skus),
      ...list(product.barcodes)
    ].filter(Boolean).join(' ');
  }

  function productUrlText(value) {
    let decodedUrl = canonicalUrl(value);
    try {
      decodedUrl = decodeURIComponent(decodedUrl);
    } catch (error) {
      // Keep malformed/native URLs usable as a weak category signal.
    }
    return decodedUrl;
  }

  function productCategoryText(product) {
    return [
      product.title,
      product.productType,
      ...list(product.tags),
      productUrlText(product.url)
    ].filter(Boolean).join(' ');
  }

  function hasInnerTubeProductSignal(product) {
    if (product.innerTubeMatch === true) return true;

    const productType = fold(product.productType).replace(/[^a-z0-9]+/g, ' ').trim();
    if (productType === 'bisiklet ic lastigi' || productType === 'bisiklet ic lastik') return true;

    const descriptiveText = [
      product.title,
      ...list(product.tags),
      productUrlText(product.url)
    ].filter(Boolean).join(' ');
    return INNER_TUBE_TERMS.some((term) => hasPhrase(descriptiveText, term));
  }

  function isNegativeInnerTubeCategory(product) {
    if (product.negativeCategory === true) return true;

    const categoryText = productCategoryText(product);
    const positive = hasInnerTubeProductSignal(product);
    const strongNegative = INNER_TUBE_NEGATIVE_TERMS.some((term) => hasPhrase(categoryText, term));
    if (strongNegative) return true;

    if (positive) return false;
    return hasPhrase(categoryText, 'jant')
      || hasPhrase(categoryText, 'dag bisikleti')
      || hasPhrase(categoryText, 'yol bisikleti')
      || hasPhrase(categoryText, 'cocuk bisikleti')
      || hasPhrase(categoryText, 'elektrikli bisiklet')
      || hasPhrase(categoryText, 'sehir bisikleti');
  }

  function isInnerTubeProduct(product) {
    return hasInnerTubeProductSignal(product) && !isNegativeInnerTubeCategory(product);
  }

  function extractSizeIntent(value) {
    const text = fold(value);
    const diameter = text.match(/(?:^|\D)(26|27(?:[.,]5)?|28|29|700)(?=\D|$)/)?.[1]?.replace(',', '.') || '';
    const width = text.match(/(?:26|27(?:[.,]5)?|28|29|700)\s*[x×]\s*(\d+(?:[.,]\d+)?)/)?.[1];
    return {
      diameter,
      width: width ? Number(width.replace(',', '.')) : null
    };
  }

  function widthRanges(value) {
    const text = fold(value);
    const ranges = [];
    const expression = /(?:26|27(?:[.,]5)?|28|29|700)(?:\s*[\/-]\s*(?:26|27(?:[.,]5)?|28|29|700))*\s*[x×]\s*(\d+(?:[.,]\d+)?)(?:\s*[-–/]\s*(\d+(?:[.,]\d+)?))?/g;
    let match;

    while ((match = expression.exec(text)) !== null) {
      const first = Number(match[1].replace(',', '.'));
      const second = match[2] ? Number(match[2].replace(',', '.')) : first;
      ranges.push([Math.min(first, second), Math.max(first, second)]);
    }
    return ranges;
  }

  function matchesWheelSize(product, size) {
    const intent = typeof size === 'object' ? size : { diameter: size, width: null };
    if (!intent?.diameter) return true;

    const text = fold([product.title, product.productType, ...list(product.tags), ...list(product.variantTitles)].join(' '));
    const escaped = String(intent.diameter).replace('.', '[.,]');
    const diameterPattern = new RegExp(`(^|[^a-z0-9])(${escaped})(?=$|[^a-z0-9]|x\\s*\\d)`, 'g');
    let diameterMatch = diameterPattern.exec(text);
    let hasDiameter = false;
    while (diameterMatch) {
      const before = text.slice(0, diameterMatch.index + diameterMatch[1].length);
      const after = text.slice(diameterMatch.index + diameterMatch[0].length);
      const contextualCode = /(?:model|seri|etrto)\s*$/.test(before)
        || /[a-z][a-z0-9]*-\s*$/.test(before);
      const contextualMeasurement = /^\s*(?:mm|cm|adet|pcs)\b/.test(after)
        || /^\s*-\s*\d{3}\b/.test(after);
      if (!contextualCode && !contextualMeasurement) {
        hasDiameter = true;
        break;
      }
      diameterMatch = diameterPattern.exec(text);
    }
    if (!hasDiameter) return false;
    if (intent.width == null) return true;

    const ranges = widthRanges(text);
    if (!ranges.length) return false;
    return ranges.some(([minimum, maximum]) => intent.width >= minimum - 0.01 && intent.width <= maximum + 0.01);
  }

  function exactMatch(values, query) {
    const target = fold(query);
    return list(values).some((value) => fold(value) === target);
  }

  function brandModelMatch(product, query) {
    const queryTokens = fold(query).replace(/[^a-z0-9]+/g, ' ').trim().split(/\s+/).filter(Boolean);
    const vendorTokens = fold(product.vendor).replace(/[^a-z0-9]+/g, ' ').trim().split(/\s+/).filter(Boolean);
    if (!vendorTokens.length || !vendorTokens.every((token) => queryTokens.includes(token))) return false;

    const remaining = [...queryTokens];
    vendorTokens.forEach((token) => {
      const index = remaining.indexOf(token);
      if (index >= 0) remaining.splice(index, 1);
    });
    const model = compact(remaining.join(' '));
    if (model.length < 2 || !/[a-z]/.test(model) || !/\d/.test(model)) return false;

    const productModelText = compact([
      product.title,
      ...list(product.variantTitles),
      ...list(product.skus)
    ].filter(Boolean).join(' '));
    return productModelText.includes(model);
  }

  function scoreProduct(product, query) {
    const info = codeParts(query);
    const foldedQuery = fold(query);
    const title = fold(product.title);
    const category = fold([product.productType, ...list(product.tags)].join(' '));
    const allText = fold(productSearchText(product));
    const queryTokens = foldedQuery.replace(/[^a-z0-9]+/g, ' ').trim().split(/\s+/).filter(Boolean);
    const titleTokens = new Set(title.replace(/[^a-z0-9]+/g, ' ').trim().split(/\s+/).filter(Boolean));
    let band = 1;
    let score = 0;

    if (product.exactBarcode || exactMatch(product.barcodes, query)) {
      band = 11;
      score += 1100;
    } else if (product.exactSku || exactMatch(product.skus, query)) {
      band = 10;
      score += 1000;
    } else if (info && compact(allText).includes(info.compact.toLowerCase())) {
      band = 9;
      score += 900;
    } else if (foldedQuery && hasPhrase(title, foldedQuery)) {
      band = 8;
      score += 800;
    } else if (queryTokens.length && queryTokens.every((token) => titleTokens.has(token))) {
      band = 7;
      score += 700;
    } else if (product.categoryMatch || (foldedQuery && hasPhrase(category, foldedQuery))) {
      band = 6;
      score += 600;
    } else if (brandModelMatch(product, query)) {
      band = 5;
      score += 500;
    }

    if (isInnerTubeIntent(query)) {
      const size = extractSizeIntent(query);
      const innerTube = isInnerTubeProduct(product);
      if (innerTube && matchesWheelSize(product, size)) {
        band = Math.max(band, 9);
        score += 950;
      } else if (innerTube && matchesWheelSize(product, { diameter: size.diameter, width: null })) {
        band = Math.max(band, 8);
        score += 800;
      } else if (innerTube) {
        band = Math.max(band, 7);
        score += 700;
      } else if (hasPhrase(product.title, 'dış lastik') || hasPhrase(product.title, 'jant') || hasPhrase(product.title, 'bisiklet')) {
        band = Math.min(band, 2);
        score -= 500;
      }
    }

    return { band, score };
  }

  function canonicalUrl(value) {
    const url = String(value || '').split('#')[0].split('?')[0];
    return url.replace(/\/$/, '');
  }

  function dedupeProducts(products) {
    const byKey = new Map();
    const merged = [];

    products.forEach((product) => {
      const key = String(product.id || canonicalUrl(product.url));
      if (!key) return;

      const existing = byKey.get(key);
      if (!existing) {
        const copy = Object.assign({}, product);
        byKey.set(key, copy);
        merged.push(copy);
        return;
      }

      existing.exactSku = Boolean(existing.exactSku || product.exactSku);
      existing.exactBarcode = Boolean(existing.exactBarcode || product.exactBarcode);
      existing.categoryMatch = Boolean(existing.categoryMatch || product.categoryMatch);
      existing.available = Boolean(existing.available || product.available);
      if (!existing.productType && product.productType) existing.productType = product.productType;
      if (!existing.vendor && product.vendor) existing.vendor = product.vendor;
      for (const property of ['tags', 'variantTitles', 'skus', 'barcodes']) {
        existing[property] = unique([...list(existing[property]), ...list(product[property])]);
      }
    });

    return merged;
  }

  function rankProducts(products, query) {
    const deduped = dedupeProducts(products);
    const eligible = isInnerTubeIntent(query)
      ? deduped.filter(isInnerTubeProduct)
      : deduped;

    return eligible
      .map((product, index) => {
        const relevance = scoreProduct(product, query);
        return Object.assign({}, product, {
          nativeRank: Number(product.nativeRank || index + 1),
          searchBand: relevance.band,
          searchScore: relevance.score
        });
      })
      .sort((left, right) => {
        if (left.searchBand !== right.searchBand) return right.searchBand - left.searchBand;
        if (left.searchScore !== right.searchScore) return right.searchScore - left.searchScore;
        if (Boolean(left.available) !== Boolean(right.available)) return left.available ? -1 : 1;
        return left.nativeRank - right.nativeRank;
      });
  }

  function rankStockFallbackProducts(products, query) {
    const sizeIntent = { diameter: '29', width: null };
    const preferredCards = [];
    const preferredByKey = new Map();

    products.forEach((product) => {
      const key = String(product.id || canonicalUrl(product.url));
      if (!key) return;

      const existingIndex = preferredByKey.get(key);
      if (existingIndex == null) {
        preferredByKey.set(key, preferredCards.length);
        preferredCards.push(product);
        return;
      }

      const existing = preferredCards[existingIndex];
      const preferredFirst = !existing.available && product.available
        ? [product, existing]
        : [existing, product];
      preferredCards[existingIndex] = dedupeProducts(preferredFirst)[0];
    });

    return rankProducts(preferredCards, query)
      .filter((product) => {
        if (matchesWheelSize(product, sizeIntent)) return true;
        return !STOCK_FALLBACK_DIAMETERS.some((diameter) => (
          matchesWheelSize(product, { diameter, width: null })
        ));
      })
      .sort((left, right) => {
        const leftSizeMatch = matchesWheelSize(left, sizeIntent);
        const rightSizeMatch = matchesWheelSize(right, sizeIntent);
        if (leftSizeMatch !== rightSizeMatch) return leftSizeMatch ? -1 : 1;
        if (Boolean(left.available) !== Boolean(right.available)) return left.available ? -1 : 1;
        if (left.searchBand !== right.searchBand) return right.searchBand - left.searchBand;
        if (left.searchScore !== right.searchScore) return right.searchScore - left.searchScore;
        return left.nativeRank - right.nativeRank;
      });
  }

  function needsStockFallback(products, query, { nativeEmpty = false } = {}) {
    if (!stockFallbackQuery(query)) return false;

    const relevant = rankProducts(products, query);
    if (!relevant.length && !nativeEmpty) return false;
    return !relevant.some((product) => Boolean(product.available));
  }

  function needsFallback(products, query) {
    const queryCandidates = candidates(query);
    if (queryCandidates.length < 2) return false;
    if (!products.length) return true;

    const info = codeParts(query);
    if (info) {
      return !products.some((product) => (
        Boolean(product.available)
        && compact(productSearchText(product)).includes(info.compact.toLowerCase())
      ));
    }

    if (isInnerTubeIntent(query)) {
      const size = extractSizeIntent(query);
      const compatible = products.filter((product) => isInnerTubeProduct(product) && matchesWheelSize(product, size));
      return compatible.length < 4 || !compatible.some((product) => Boolean(product.available));
    }

    return false;
  }

  return Object.freeze({
    INNER_TUBE_NEGATIVE_TERMS,
    MODEL_PREFIXES,
    TURKISH_ASCII_PAIRS,
    candidates,
    canonicalUrl,
    brandModelMatch,
    cleanQuery,
    codeCandidates,
    codeParts,
    compact,
    dedupeProducts,
    fold,
    hasAdvancedSearchSyntax,
    extractSizeIntent,
    isInnerTubeIntent,
    isInnerTubeProduct,
    isNegativeInnerTubeCategory,
    naturalFallback,
    needsFallback,
    needsStockFallback,
    matchesWheelSize,
    rankProducts,
    rankStockFallbackProducts,
    scoreProduct,
    stockFallbackQuery
  });
});
